import React, { useState, useEffect } from "react";
import { Dropdown, Modal, Button, Form, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";

import {
  addContent,
  getContents,
  updateContent,
  deleteContent,
} from "../../../FIREBASECONFIG";

const WebsiteContent = () => {
  const [contentData, setContentData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true); 
  const itemsPerPage = 15;

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editContent, setEditContent] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [file, setFile] = useState(null);

  // 🔹 Fetch Content Collection
  const fetchContent = async () => {
    try {
      setLoading(true);
      const data = await getContents("Content");
      setContentData(data);
    } catch (error) {
      console.error("Error fetching content:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  // 🔹 Handle Save (Add/Edit)
  const handleSave = async (e) => {
    e.preventDefault();
    const form = e.target;

    try {
      const newContent = {
        section: form.section.value,
        heading: form.heading.value,
        subheading: form.description.value, // 🔹 save as "subheading"
        image: editContent?.image || "",
        alt: form.altText.value || "Website image", // 🔹 save as "alt"
      };

      if (editContent) {
        // 🔹 Update document
        await updateContent("Content", "ContentImages", editContent.id, newContent, file);
      } else {
        // 🔹 Add new document
        await addContent("Content", "ContentImages", newContent, file);
      }

      setShowModal(false);
      setEditContent(null);
      setPreviewImage(null);
      setFile(null);
      fetchContent(); // refresh data
    } catch (error) {
      console.error("Error saving content:", error);
    }
  };

  // 🔹 Handle Delete
  const handleDelete = async (id) => {
    try {
      await deleteContent("Content", id);
      setContentData((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error("Error deleting content:", error);
    }
  };

  // 🔹 Handle File Change
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreviewImage(URL.createObjectURL(selectedFile));
    }
  };

  // 🔹 Filter + Paginate
  const filteredContent = contentData.filter((c) => {
    const lower = searchTerm.toLowerCase();
    return (
      c.section?.toLowerCase().includes(lower) ||
      c.heading?.toLowerCase().includes(lower) ||
      c.subheading?.toLowerCase().includes(lower)
    );
  });

  const totalPages = Math.ceil(filteredContent.length / itemsPerPage);
  const paginatedContent = filteredContent.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  return (
    <div className="col-12">
      <div className="card">
        {/* Header */}
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title m-0">Website Content</h4>
          <div className="d-flex align-items-center gap-2">
            <input
              type="text"
              className="form-control"
              placeholder="Search content"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
            />
            <button
              className="btn btn-secondary btn-md"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => {
                setEditContent(null);
                setPreviewImage(null);
                setFile(null);
                setShowModal(true);
              }}
            >
              Add Content
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="card-body">
          {loading ? (
            // 🔹 Show centered loading spinner
            <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Image</th>
                    <th>Section</th>
                    <th>Heading</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedContent.length > 0 ? (
                    paginatedContent.map((item, i) => (
                      <tr key={item.id}>
                        <td>{currentPage * itemsPerPage + i + 1}</td>
                        <td>
                          <img
                            src={item.image}
                            alt={item.alt || "Website image"}
                            style={{ width: "60px", height: "40px", borderRadius: "6px" }}
                          />
                        </td>
                        <td>{item.section}</td>
                        <td>{item.heading}</td>
                        <td style={{ maxWidth: "250px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {item.subheading}
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle variant="link">
                              &#x2022;&#x2022;&#x2022;
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                              <Dropdown.Item
                                onClick={() => {
                                  setEditContent(item);
                                  setPreviewImage(item.image);
                                  setShowModal(true);
                                }}
                              >
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleDelete(item.id)}
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No content found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && filteredContent.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Showing{" "}
                {filteredContent.length === 0
                  ? 0
                  : currentPage * itemsPerPage + 1}{" "}
                to{" "}
                {Math.min(
                  (currentPage + 1) * itemsPerPage,
                  filteredContent.length
                )}{" "}
                of {filteredContent.length} entries
              </div>
              <div>
                <button
                  className={`btn btn-sm btn-outline-primary me-2 ${
                    currentPage === 0 ? "disabled" : ""
                  }`}
                  onClick={() =>
                    currentPage > 0 && setCurrentPage(currentPage - 1)
                  }
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={`btn btn-sm border me-2 ${
                      currentPage === i ? "btn-primary text-white" : ""
                    }`}
                    onClick={() => setCurrentPage(i)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className={`btn btn-sm btn-outline-primary ${
                    currentPage === totalPages - 1 ? "disabled" : ""
                  }`}
                  onClick={() =>
                    currentPage + 1 < totalPages && setCurrentPage(currentPage + 1)
                  }
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editContent ? "Edit Content" : "Add Content"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Section</Form.Label>
              <Form.Control
                name="section"
                defaultValue={editContent?.section || ""}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Heading</Form.Label>
              <Form.Control
                name="heading"
                defaultValue={editContent?.heading || ""}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                defaultValue={editContent?.subheading || ""}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Image</Form.Label>
              <Form.Control
                type="file"
                name="image"
                accept="image/*"
                onChange={handleFileChange}
              />
              {(previewImage || editContent?.image) && (
                <div className="mt-2">
                  <img
                    src={previewImage || editContent?.image}
                    alt="Preview"
                    style={{ width: "120px", borderRadius: "8px" }}
                  />
                </div>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Alt Text</Form.Label>
              <Form.Control
                name="altText"
                defaultValue={editContent?.alt || ""}
                placeholder="Enter alt text for image"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default WebsiteContent;